import { Controller } from "@hotwired/stimulus"

// Manages collapsible proxy entries sections with localStorage persistence
export default class extends Controller {
  static targets = ["content", "icon"]
  static values = {
    sectionId: String,
    defaultExpanded: { type: Boolean, default: false },
  }

  connect() {
    // Initialize state from localStorage or use default
    const savedState = this.getSavedState()
    if (savedState !== null) {
      this.expanded = savedState
    } else {
      this.expanded = this.defaultExpandedValue
    }

    this.updateUI(false) // Initial render without animation

    // Observe the content for new entries being added (via turbo stream)
    this.setupMutationObserver()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupMutationObserver() {
    // Find the list element inside the content
    const list = this.contentTarget.querySelector("ul")
    if (!list) return

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added node is an actual entry (not the empty message)
          const hasNewEntry = Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              node.tagName === "LI" &&
              !node.id?.includes("empty"),
          )

          if (hasNewEntry && !this.expanded) {
            this.expanded = true
            this.saveState()
            this.updateUI(true)
          }
        }
      }
    })

    this.observer.observe(list, { childList: true })
  }

  toggle() {
    this.expanded = !this.expanded
    this.saveState()
    this.updateUI(true) // Animate the transition
  }

  updateUI(animate) {
    const content = this.contentTarget
    const icon = this.iconTarget

    if (this.expanded) {
      // Expand
      // First measure the content height
      content.style.maxHeight = "none"
      const height = content.scrollHeight

      if (animate) {
        // Reset to 0 first for animation
        content.style.maxHeight = "0px"
        // Force reflow
        content.offsetHeight
        // Then animate to full height
        content.style.maxHeight = height + "px"
        content.style.opacity = "1"

        // After animation, set to none for dynamic content
        setTimeout(() => {
          if (this.expanded) {
            content.style.maxHeight = "none"
          }
        }, 300)
      } else {
        content.style.maxHeight = "none"
        content.style.opacity = "1"
      }
      icon.classList.add("rotate-180")
    } else {
      // Collapse
      if (animate) {
        // First set explicit height from current state
        content.style.maxHeight = content.scrollHeight + "px"
        // Force reflow
        content.offsetHeight
        // Then collapse
        content.style.maxHeight = "0"
        content.style.opacity = "0"
      } else {
        content.style.maxHeight = "0"
        content.style.opacity = "0"
      }
      icon.classList.remove("rotate-180")
    }
  }

  getSavedState() {
    const key = this.storageKey()
    const value = localStorage.getItem(key)
    if (value === null) return null
    return value === "true"
  }

  saveState() {
    const key = this.storageKey()
    localStorage.setItem(key, this.expanded.toString())
  }

  storageKey() {
    return `proxy_entries_section_${this.sectionIdValue}_expanded`
  }
}
